import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Share2, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ShareAssessmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
  assessmentTitle: string;
  assessmentSlug: string;
}

export default function ShareAssessmentDialog({
  open,
  onOpenChange,
  assessmentId,
  assessmentTitle,
  assessmentSlug,
}: ShareAssessmentDialogProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const userName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'A user';

      const { data, error } = await supabase.functions.invoke('share-assessment', {
        body: {
          assessmentId,
          assessmentTitle,
          assessmentSlug,
          sharedWithEmail: email.trim().toLowerCase(),
          sharedByName: userName,
          sharedByEmail: user.email,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSuccess(true);
      toast({
        title: 'Assessment shared!',
        description: data?.isExistingUser
          ? `${email} has been notified and can view this assessment.`
          : `An invitation has been sent to ${email} with a link to set up their account.`,
      });

      setTimeout(() => {
        setEmail('');
        setSuccess(false);
        onOpenChange(false);
      }, 1500);
    } catch (err: any) {
      console.error('Share error:', err);
      toast({
        variant: 'destructive',
        title: 'Failed to share',
        description: err.message || 'Could not share assessment. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) { onOpenChange(v); setEmail(''); setSuccess(false); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Share Assessment
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Share <span className="font-medium text-foreground">"{assessmentTitle}"</span> with someone. They'll receive an email notification to collaborate.
        </p>

        <form onSubmit={handleShare} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="share-email">Recipient's email</Label>
            <Input
              id="share-email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || success}
              required
            />
          </div>

          <Button type="submit" className="w-full gap-2" disabled={loading || success || !email.trim()}>
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Sharing...</>
            ) : success ? (
              <><CheckCircle2 className="h-4 w-4" /> Shared!</>
            ) : (
              <><Share2 className="h-4 w-4" /> Share Assessment</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
